import * as React from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Button } from "@/components/ui/button";

/** How often to ask the browser to re-fetch sw.js while the app is open. */
const SW_UPDATE_INTERVAL_MS = 30_000;

/**
 * Prompt-style SW update — avoids silent reload mid-edit (registerType: prompt).
 * After the user accepts, updateServiceWorker(true) skipWaiting + reload so the
 * lazy-chunk graph matches the new precache (#5).
 *
 * First install: when there is no controller yet, activate ASAP so offline
 * NavigationRoute works without a second visit (#1).
 *
 * Update detection: browsers only check SW on navigation by default — poll
 * registration.update() + check on focus/visibility/online so deploy → toast
 * is seconds, not “wait forever / reopen the app”.
 */
export function PwaUpdateToast() {
  const registrationRef = React.useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      registrationRef.current = registration;

      // First-time install: no active controller → claim as soon as installed
      if (!navigator.serviceWorker.controller && registration.installing) {
        const sw = registration.installing;
        sw.addEventListener("statechange", () => {
          if (sw.state === "installed" && registration.waiting) {
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
          }
        });
      }

      // Already waiting from a prior visit (e.g. toast dismissed) — surface again
      if (registration.waiting) {
        setNeedRefresh(true);
      }

      void registration.update().catch(() => undefined);
    }
  });

  React.useEffect(() => {
    const checkForUpdate = () => {
      const reg = registrationRef.current;
      if (!reg) return;
      void reg.update().catch(() => undefined);
      if (reg.waiting) setNeedRefresh(true);
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };

    const intervalId = window.setInterval(checkForUpdate, SW_UPDATE_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", checkForUpdate);
    window.addEventListener("online", checkForUpdate);
    window.addEventListener("pageshow", checkForUpdate);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", checkForUpdate);
      window.removeEventListener("online", checkForUpdate);
      window.removeEventListener("pageshow", checkForUpdate);
    };
  }, [setNeedRefresh]);

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      className="fixed bottom-24 left-4 right-4 z-[60] mx-auto max-w-md rounded-xl border border-amber-300 bg-amber-50 p-3 shadow-lg dark:border-amber-800 dark:bg-amber-950 lg:bottom-6"
    >
      <div className="text-sm font-medium text-amber-950 dark:text-amber-100">
        Új verzió elérhető
      </div>
      <p className="mt-1 text-xs text-amber-900/80 dark:text-amber-200/80">
        Frissíts, ha befejezted a szerkesztést — így nem veszíted el a folyamatban lévő munkát.
      </p>
      <div className="mt-3 flex gap-2">
        <Button
          type="button"
          variant="accent"
          onClick={() => void updateServiceWorker(true)}
          data-testid="pwa-update-button"
        >
          Frissítés
        </Button>
        <Button type="button" variant="outline" onClick={() => setNeedRefresh(false)}>
          Később
        </Button>
      </div>
    </div>
  );
}
